import { useState } from "react";
import type { Workshop } from "@/content/workshops";
import {
  modalContentByWorkshop,
  type AgendaContent,
  type AgendaItem,
} from "@/features/workshop/modals/modal-content";
import { resolveField } from "../workshop-override-content";
import {
  clearEffectiveOverrideField,
  setEffectiveOverride,
  useEffectiveWorkshopOverride,
} from "@/stores/useEffectiveWorkshopOverride";
import { AddButton, Field, ListCard, SectionFrame, Select, TextInput } from "../editor-kit";
import { moveItem, sameJson } from "../editor-utils";

const EMPTY_SESSION: AgendaItem = {
  time: "09:00",
  title: "",
  detail: "",
  durationMin: 30,
  type: "session",
};

/** Workshop agenda editor — top-level date/venue/time + a list of sessions. */
export function AgendaEditor({ wsId }: { wsId: Workshop["id"] }) {
  const override = useEffectiveWorkshopOverride(wsId);
  const def = modalContentByWorkshop[wsId].agenda;
  const current = resolveField(override, "agenda", def);
  const [draft, setDraft] = useState<AgendaContent>(current);

  const setTop = (patch: Partial<AgendaContent>) => setDraft((d) => ({ ...d, ...patch }));
  const setSession = (i: number, patch: Partial<AgendaItem>) =>
    setDraft((d) => ({
      ...d,
      sessions: d.sessions.map((s, j) => (j === i ? { ...s, ...patch } : s)),
    }));

  return (
    <SectionFrame
      description="The agenda shown in the Workshop Agenda modal."
      overridden={override?.agenda !== undefined}
      dirty={!sameJson(draft, current)}
      onSave={() => setEffectiveOverride(wsId, { agenda: draft })}
      onReset={() => {
        clearEffectiveOverrideField(wsId, "agenda");
        setDraft(def);
      }}
    >
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Field label="Date">
          <TextInput value={draft.date} onChange={(e) => setTop({ date: e.target.value })} />
        </Field>
        <Field label="Venue">
          <TextInput value={draft.venue} onChange={(e) => setTop({ venue: e.target.value })} />
        </Field>
        <Field label="Time range">
          <TextInput
            value={draft.timeRange}
            onChange={(e) => setTop({ timeRange: e.target.value })}
          />
        </Field>
      </div>

      {draft.sessions.map((s, i) => (
        <ListCard
          key={i}
          title={`Item ${i + 1}`}
          onMoveUp={i > 0 ? () => setTop({ sessions: moveItem(draft.sessions, i, -1) }) : undefined}
          onMoveDown={
            i < draft.sessions.length - 1
              ? () => setTop({ sessions: moveItem(draft.sessions, i, 1) })
              : undefined
          }
          onRemove={() => setTop({ sessions: draft.sessions.filter((_, j) => j !== i) })}
        >
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Field label="Time">
              <TextInput value={s.time} onChange={(e) => setSession(i, { time: e.target.value })} />
            </Field>
            <Field label="Type">
              <Select
                value={s.type}
                onChange={(v) => setSession(i, { type: v as AgendaItem["type"] })}
                options={[
                  { value: "session", label: "session" },
                  { value: "break", label: "break" },
                ]}
              />
            </Field>
          </div>
          <Field label="Title">
            <TextInput value={s.title} onChange={(e) => setSession(i, { title: e.target.value })} />
          </Field>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <Field label="Detail">
              <TextInput
                value={s.detail}
                onChange={(e) => setSession(i, { detail: e.target.value })}
              />
            </Field>
            <Field label="Mins">
              <TextInput
                type="number"
                value={s.durationMin}
                onChange={(e) => setSession(i, { durationMin: Number(e.target.value) || 0 })}
                className="w-20"
              />
            </Field>
          </div>
        </ListCard>
      ))}
      <AddButton
        label="Add agenda item"
        onClick={() => setTop({ sessions: [...draft.sessions, { ...EMPTY_SESSION }] })}
      />
    </SectionFrame>
  );
}
